<script lang="ts">
  import type { Distributor } from "@shared/types/Distributor";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import { InventoryInformationCardType } from "./InventoryInformationCardType";
  import InformationCard from "./InformationCard.svelte";
  import {
    DistributorEntity,
    DistributorPLPModalEntity,
    DistributorPLPModalType,
    DistributorPLPModalValue,
  } from "@shared/services/event_tracking/entities";
  import { v4 as uuidv4 } from "uuid";

  export let showLowInventoryUpdatesCard: boolean = true;
  export let distributor: Distributor;

  const inventoryCardId = `information-card-inventory-${uuidv4()}`;

  //@ts-ignore
  const constants = window.C;

  $: inventoryInformationCardType = constants.HAS_HOURLY_INVENTORY_UPDATES_DISTRIBUTOR_IDS.includes(
    distributor.id,
  )
    ? InventoryInformationCardType.hourly
    : constants.HAS_DAILY_INVENTORY_UPDATES_DISTRIBUTOR_IDS.includes(distributor.id)
    ? InventoryInformationCardType.daily
    : constants.HAS_WEEKLY_INVENTORY_UPDATES_DISTRIBUTOR_IDS.includes(distributor.id)
    ? InventoryInformationCardType.weekly
    : constants.HAS_MONTHLY_INVENTORY_UPDATES_DISTRIBUTOR_IDS.includes(distributor.id)
    ? InventoryInformationCardType.monthly
    : constants.NO_RECENT_INVENTORY_UPDATES_DISTRIBUTOR_IDS.includes(distributor.id)
    ? InventoryInformationCardType.noRecentUpdates
    : InventoryInformationCardType.notIntegratedDistributor;
  $: shouldShowCard =
    showLowInventoryUpdatesCard ||
    (inventoryInformationCardType !== InventoryInformationCardType.monthly &&
      inventoryInformationCardType !== InventoryInformationCardType.noRecentUpdates &&
      inventoryInformationCardType !== InventoryInformationCardType.notIntegratedDistributor);

  $: inventoryDetailsEntities = distributor?.id
    ? [
        new DistributorPLPModalEntity({
          modalType: DistributorPLPModalType.Inventory,
          // These enums have overlap in values but are different types so casting to unknown and then to the correct type prevents the need for
          value: inventoryInformationCardType as unknown as DistributorPLPModalValue,
        }),
        DistributorEntity.fromDistributor(distributor),
      ]
    : undefined;
  const getUpdateLevelIconClass = (type: InventoryInformationCardType | undefined): string => {
    if (!type) return "";

    switch (type) {
      case InventoryInformationCardType.every15minutes:
      case InventoryInformationCardType.hourly:
      case InventoryInformationCardType.daily:
        return "frequent-updates";
      case InventoryInformationCardType.weekly:
        return "weekly-updates";
      case InventoryInformationCardType.monthly:
      case InventoryInformationCardType.noRecentUpdates:
      case InventoryInformationCardType.notIntegratedDistributor:
      default:
        return "rare-updates";
    }
  };

  const getInventoryHeading = (type: InventoryInformationCardType | undefined): string => {
    if (!type) return "";
    switch (type) {
      case InventoryInformationCardType.every15minutes:
      case InventoryInformationCardType.hourly:
        return "Hourly Inventory Updates";
      case InventoryInformationCardType.daily:
        return "Daily Inventory Updates";
      case InventoryInformationCardType.weekly:
        return "Weekly Inventory Updates";
      case InventoryInformationCardType.monthly:
        return "Monthly Inventory Updates";
      case InventoryInformationCardType.noRecentUpdates:
        return "No Recent Inventory Updates";
      case InventoryInformationCardType.notIntegratedDistributor:
      default:
        return "Inventory Levels Not Shared";
    }
  };
  const getInventoryDescription = (type: InventoryInformationCardType | undefined): string => {
    if (!type) return "";
    switch (type) {
      case InventoryInformationCardType.every15minutes:
      case InventoryInformationCardType.hourly:
        return "This distributor shares inventory levels hourly.";
      case InventoryInformationCardType.daily:
        return "This distributor shares inventory levels daily.";
      case InventoryInformationCardType.weekly:
        return "This distributor shares inventory levels weekly.";
      case InventoryInformationCardType.monthly:
      case InventoryInformationCardType.noRecentUpdates:
        return "Check with your rep for product inventory.";
      case InventoryInformationCardType.notIntegratedDistributor:
      default:
        return "This distributor does not share inventory with Provi.";
    }
  };
  const getInventoryModalText = (type: InventoryInformationCardType | undefined): string => {
    if (!type) return "";
    switch (type) {
      case InventoryInformationCardType.every15minutes:
        return "This distributor shares inventory levels with Provi every 15 minutes. The inventory level at the time the order is fulfilled may vary. Your rep may contact you in the event a product cannot be fulfilled.";
      case InventoryInformationCardType.hourly:
        return "This distributor shares inventory levels with Provi every hour. The inventory level at the time the order is fulfilled may vary. Your rep may contact you in the event a product cannot be fulfilled.";
      case InventoryInformationCardType.daily:
        return "This distributor shares inventory levels with Provi every day. The inventory level at the time the order is fulfilled may vary. Your rep may contact you in the event a product cannot be fulfilled.";
      case InventoryInformationCardType.weekly:
        return "This distributor shares inventory levels with Provi every week. The inventory level at the time the order is fulfilled may vary. Your rep may contact you in the event a product cannot be fulfilled.";
      case InventoryInformationCardType.monthly:
        return "This distributor shares inventory levels with Provi every month. Contact your rep to ensure a product is in stock. The inventory level at the time the order is fulfilled may vary. Your rep may contact you in the event a product cannot be fulfilled.";
      case InventoryInformationCardType.noRecentUpdates:
        return "This distributor hasn't shared inventory levels with Provi recently. Contact your rep to ensure a product is in stock. The inventory level at the time the order is fulfilled may vary. Your rep may contact you in the event a product cannot be fulfilled.";
      case InventoryInformationCardType.notIntegratedDistributor:
      default:
        return "This distributor does not share inventory levels with Provi. Contact your rep to ensure a product is in stock. The inventory level at the time the order is fulfilled may vary. Your rep may contact you in the event a product cannot be fulfilled.";
    }
  };
</script>

<style>
  :global(.icon-container.frequent-updates) {
    background-color: #dde2f8;
  }
  :global(.icon-container.weekly-updates) {
    background-color: #fde7ba;
  }
  :global(.icon-container.rare-updates) {
    background-color: #eff0f0;
  }

  :global(.icon-container.frequent-updates svg) {
    color: #243ca8;
  }
  :global(.icon-container.weekly-updates svg),
  :global(.icon-container.rare-updates svg) {
    color: #595959;
  }

  :global([data-testid="inventory-details-modal"] footer button:first-child),
  :global([data-testid="inventory-details-modal"] footer > div > button:first-child) {
    display: none !important;
  }
</style>

<InformationCard
  shouldShow={shouldShowCard}
  iconContainerClass={getUpdateLevelIconClass(inventoryInformationCardType)}
  heading={getInventoryHeading(inventoryInformationCardType)}
  description={getInventoryDescription(inventoryInformationCardType)}
  modalTitle={getInventoryHeading(inventoryInformationCardType)}
  modalDescription={getInventoryModalText(inventoryInformationCardType)}
  modalTestId="inventory-details-modal"
  trackingEventName="distributor_plp_inventory_card_see_details_button"
  trackingEntities={inventoryDetailsEntities}
  trackingId={inventoryCardId}
>
  <svelte:fragment slot="icon">
    <Icon type="Integrated" width={40} height={40} />
  </svelte:fragment>
</InformationCard>
